/**
 *
 * FBForms
 *
 */

import React, { memo, useCallback, useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useTranslation} from "react-i18next";

// styles
import "./style.scss";

// antd component
import { Form, Input, Dropdown, Button } from "antd";
const { TextArea } = Input;

// icons
import CustomIcons from "components/CustomIcons";

// components
import ErrorBoundary from "engine/ErrorBoundary";
import HiddenField from "components/Forms/Fields/HiddenField";

// Communication components
import {
  FBSectionHeader,
  FBSectionErroMessage,
  FBSectionFieldActions,
} from "components/Communications";
// inner components
import SectionFields from "./components/SectionFields";

// hooks
import { useRefState } from "utils/hooks";

function validateForm(formData, name = "forms") {
  let isValid = true;
  // code for validation title
  /* const formTitle = formData?.[`${name}-title`] || "";
  const regex = /<[^>]*>|^\s*$/gm; // match any HTML tags and whitespace
  const actualTitle = formTitle?.replace(regex, "")?.replace("\n", ""); // remove any HTML tags and whitespace
  const containsActualTitle = !!formTitle && !!actualTitle && actualTitle?.length > 0; */
  /* if (!containsActualTitle) {
    isValid = false;
  }
  else  */if (
    Array.isArray(formData?.[`${name}-fields`]) &&
    formData?.[`${name}-fields`]?.length > 0
  ) {
    for (let field of formData?.[`${name}-fields`]) {
      if (!field?.["field-title"]) {
        isValid = false;
      }
      
      else if (!!!field?.["field-type"]) {
        isValid = false;
      }

      else if (
        field?.["field-type"] == "radio" ||
        field?.["field-type"] == "checkbox"
      ) {
        if (
          !field?.["field-input"] ||
          field?.["field-input"]?.some(
            input => !input?.[`${field?.["field-type"]}-input`],
          )
        ) {
          isValid = false;
          break;
        }
      } 
    }
  } else {
    isValid = false;
  }
  return isValid;
}

function FBForms({
  name,
  mode,
  method,
  initialValues,
  isValidSection,
  // actions
  setValidSection,
  onDelete,
  onChangeSection,
  // default props
  className,
  ...rest
}) {
  const childClassNames = classNames(
    "tcf-fb-forms-group",
    "tcf-fb-group",
    className,
  );
  const { t, i18n } = useTranslation();

  let formName = name;

  const [dynamicForm] = Form.useForm();

  const [mounted, setMounted] = useState(false);

  const [validFields, setValidFields] = useState(isValidSection || false);
  const [currentValues, currentValuesRef, setCurrentValues] = useRefState(
    initialValues || {},
  );

  useEffect(() => {
    setMounted(true);

    if (!!initialValues) {
      const isValid = validateForm(initialValues);
      if (!isValid) {
        dynamicForm.validateFields();
      }
      setValidFields(isValid);
      !!setValidSection && setValidSection(name, isValid);
      if (JSON.stringify(initialValues) != JSON.stringify(currentValues)) {
        setCurrentValues(initialValues);
      }
    } else if (!!!initialValues) {
      resetAllStates();
    }

    return () => {
      resetAllStates();
    };
  }, []);

  const resetAllStates = useCallback(() => {
    setCurrentValues({});
    setValidFields(false);
    dynamicForm.resetFields();
  }, []);

  // Delete Section
  const deleteSection = useCallback(name => {
    resetAllStates();
    // update Section
    !!onChangeSection && onChangeSection(name, null);
    !!onDelete && onDelete(name);
  }, [name, onDelete, onChangeSection]);

  // custom Validation for Section fileds
  const checkValidCallback = useCallback(
    async (rule, values, callback) => {
      const isValid = validateForm(currentValuesRef?.curent || currentValues) && values?.length > 0;
      setValidFields(isValid);
      !!setValidSection && setValidSection(name, isValid);
      if (isValid) {
        return Promise.resolve(isValid);
      } else {
        return Promise.reject(new Error(t("errors.entryNotValid")), values);
      }
    },
    [setValidSection, currentValues, currentValuesRef],
  );

  const erroMessage = useMemo(
    () => (
      <FBSectionErroMessage
        showMessage={!validFields}
        message={t("inputs.sectionNeedsMultipleFields")}
      />
    ),
    [validFields, isValidSection, i18n?.language],
  );

  // Update parent states based on changes inside Form
  const handleValuesChange = useCallback(
    async (changedValues, allValues) => {
      let values = allValues[name] || {};
      const isValid = validateForm(values);
      setValidFields(isValid);
      !!setValidSection && setValidSection(name, isValid);
      !!isValid && setCurrentValues(values);
      // dynamicForm.validateFields();
    },
    [name, currentValues, setValidSection],
  );

  /* Generate Filed based on chozen type */
  const generateRadioOrCheckboxFiled = useCallback((element, fieldType, rowIndex) => {
    const { key, name, ...restField } = element;
    return (
      <>
        <Form.List
          {...restField}
          name={[name, "field-input"]}
          key={[key, "field-input"]}
          className={`forms-field ${fieldType}`}
          rules={[
            {
              required: true,
              validator: async (_, vals) => {
                if (!!!vals || vals?.length < 2) {
                  return Promise.reject(new Error(t("errors.atLeast2Option")));
                }
              },
              validateTrigger: "onChange",
            },
          ]}
        >
          {(fields, { add, remove, move }) => {
            return (
              <>
                {fields.map((element, index) => {
                  const { name, key, ...restField } = element;
                  return (
                    <div
                      key={`forms-row-${key}_${index}-key-${rowIndex}-row`}
                      className={`forms-row forms-row-${index} ${fieldType}`}
                      align="baseline"
                    >
                      <Form.Item
                        {...restField}
                        name={[name, [`${fieldType}-input`]]}
                        key={[key, [`${fieldType}-input`]]}
                        className="forms-field forms-field-title"
                        rules={[
                          {
                            required: true,
                            message: t("inputs.mandatoryField"),
                            validateTrigger: "onChange",
                          },
                        ]}
                      >
                        <Input placeholder={`Option`} />
                      </Form.Item>
                      <div className="forms-row__actions">
                        {fields?.length > 2 && (
                          <CustomIcons
                            className="dynamic-delete-button"
                            type="garbage"
                            onClick={() => remove(name)}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}

                <Button className="add-sub-options" onClick={() => add()}>
                  {t("inputs.addOption")}
                </Button>
              </>
            );
          }}
        </Form.List>
      </>
    );
  }, []);

  const getRowsValueType = useCallback((name) => { 
    let cur = dynamicForm.getFieldsValue();
    let curV = Object.keys(cur)?.length > 0 ? cur : { [formName]: currentValues };
    let filedsValues = curV[formName][`${formName}-fields`] || [];
    let rowValsType = filedsValues?.length > 0 && filedsValues[name]?.["field-type"];
    return rowValsType;
  }, [formName, currentValues]);

  const generateFileldByType = useCallback(
    (element, rowIndex) => {
      const { key, name, ...restField } = element;
      const rowValsType =  getRowsValueType(name);

      switch (rowValsType) {
        case "input":
          return (
            <>
              <Form.Item
                {...restField}
                name={[name, "field-input"]}
                key={[key, "field-input"]}
                className="forms-field"
                rules={[
                  {
                    required: false,
                  },
                ]}
              >
                <Input disabled placeholder={t("inputs.response")} />
              </Form.Item>
            </>
          );
        case "radio":
          return generateRadioOrCheckboxFiled(element, "radio", rowIndex);
        case "checkbox":
          return generateRadioOrCheckboxFiled(element, "checkbox", rowIndex);
        case "textarea":
          return (
            <>
              <Form.Item
                {...restField}
                name={[name, "field-input"]}
                key={[key, "field-input"]}
                className="forms-field"
                rules={[
                  {
                    required: false,
                  },
                ]}
              >
                <TextArea disabled row="2" placeholder={t("inputs.response")} />
              </Form.Item>
            </>
          );
        default:
          return <></>;
      }
    },
    [currentValues],
  );

  /* Add new Filed Row */
  const fieldTypeList = [
    { label: t("inputs.response"), key: "textarea" }, // Longue réponse
    { label: t("inputs.multipleChoice"), key: "checkbox" },
    { label: t("inputs.uniqueChoice"), key: "radio" },
   // { label: t("inputs.response"), key: "input" }, // Courte réponse
  ];
  const addNewTypeField = useCallback(
    (type, addCalback) => {
      let defVals = {
        "field-title": "",
        "field-type": type,
        "field-input": null,
      };
      if (type == "radio") {
        defVals["field-input"] = [
          { [`${type}-input`]: null },
          { [`${type}-input`]: null },
        ];
      } else if (type == "checkbox") {
        defVals["field-input"] = [
          { [`${type}-input`]: null },
          { [`${type}-input`]: null },
        ];
      }
      !!setValidSection && setValidSection(name, false);
      setValidFields(false);
      addCalback(defVals);
    },
    [setValidSection, name],
  );
  const dorpdownAddNewRow = useCallback(
    addCallback => (
      <div className="add-new-filed__wrapper">
        <Dropdown
          menu={{ items: fieldTypeList }}
          trigger={["hover"]}
          className="add-new-filed"
          overlayClassName="add-new-filed__dropdown"
          getPopupContainer={trigger => trigger.parentElement}
          dropdownRender={menu => {
            const items = menu.props.items;
            return (
              <div className="dropdown-content">
                {items?.map(({ key, label }) => {
                  return (
                    <div
                      className="dropdown-content__item"
                      onClick={() => addNewTypeField(key, addCallback)}
                    >
                      {label}
                    </div>
                  );
                })}
              </div>
            );
          }}
          placement="top"
          arrow={{
            pointAtCenter: true,
          }}
        >
          <Button>
            <span className="add-new-filed__btn">{t("inputs.addField")}</span>
          </Button>
        </Dropdown>
      </div>
    ),
    [addNewTypeField],
  );

  const renderFormList = useCallback((fields, { add, remove, move }) => { 
    return (
      <>
        {fields.map((element, subIndex) => {
          const { key, name, ...restField } = element;
          const fieldType = currentValues?.[`${formName}-fields`][name]?.["field-type"];
          const groupKey = `${fieldType}-${name}-${subIndex}`
          return (
            <div
              key={groupKey}
              className="forms-group"
              align="baseline"
            >
              <FBSectionFieldActions
                key={`${groupKey}-group-action`}
                type="forms"
                index={name}
                fieldType={fieldType}
                fieldTypeList={fieldTypeList}
                totalLength={
                  currentValues[`${formName}-fields`]?.length || 0
                }
                onMove={move}
                onDelete={() => remove(name)}
              />

              <div className="forms-group__context">
                <HiddenField
                  key={[name, `field-type`]}
                  data={{
                    name: [name, `field-type`],
                  }}
                  index={0}
                />

                <Form.Item
                  {...restField}
                  name={[name, "field-title"]}
                  key={[key, "field-title"]}
                  className="forms-title"
                  rules={[
                    {
                      required: true,
                      message: t("inputs.mandatoryField"),
                      validateTrigger: "onChange",
                    },
                  ]}
                >
                  <Input placeholder={t("inputs.fieldTitle")} />
                </Form.Item>

                {generateFileldByType(element, subIndex)}
              </div>
            </div>
          );
        })}

        {dorpdownAddNewRow(add)}
      </>
    );
  }, [i18n?.language]);

  return (
    <div className={childClassNames}>
      <Form
        name={formName}
        form={dynamicForm}
        initialValues={{ [formName]: currentValues }}
        className="tcf-form-builder"
        autoComplete="off"
        onValuesChange={handleValuesChange}
      >
        <FBSectionHeader
          key="forms-section-header"
          type="forms"
          onDelete={() => deleteSection(formName)}
        />
        <div className="tcf-fb-group">
          <SectionFields
            key="forms-section-header"
            name={formName}
            sectionValues={currentValues}
            required={false}
          />
          <ErrorBoundary>
            <Form.List
              className="tcf-fb-group"
              key={`tcf-fb-group-${formName}`}
              name={[formName, `${formName}-fields`]}
              rules={[
                {
                  required: true,
                  validator: checkValidCallback,
                  validateTrigger: "onChange",
                  message: t("inputs.mandatoryField"),
                },
              ]}
            >
              {(fields, { add, remove, move }) => renderFormList(fields, { add, remove, move })}
            </Form.List>
          </ErrorBoundary>
          {erroMessage}
        </div>
      </Form>
    </div>
  );
}

FBForms.defaultProps = {
  name: "forms",
  mode: "new",
  method: "new",
};
FBForms.propTypes = {
  onDelete: PropTypes.func,
};

export default memo(FBForms);
